import { Helmet } from "react-helmet-async";

type Props = {
  /** Fallback single-URL candidate (the bounded baseline JPEG). */
  src: string;
  /** Best-format responsive candidate set (usually AVIF). */
  imageSrcSet?: string;
  /** Must match the `sizes` attribute on the hero <img> exactly. */
  sizes: string;
  /** MIME type of `imageSrcSet` so the browser can skip unsupported rungs. */
  type?: string;
};

/**
 * Preloads the above-the-fold hero portrait as a high-priority image so the
 * LCP candidate starts downloading during HTML parse instead of after the
 * React bundle hydrates and mounts <HeroPortrait />.
 *
 * `imagesrcset` + `imagesizes` must mirror the hero <img> attributes exactly,
 * otherwise the browser resolves a different candidate and downloads the hero
 * twice — a net regression. Render at most one of these per page.
 */
export const HeroPreload = ({ src, imageSrcSet, sizes, type = "image/avif" }: Props) => {
  // Helmet forwards unknown props verbatim to the rendered tag, which is how
  // we emit the lowercase `imagesrcset` / `fetchpriority` attributes the HTML
  // spec defines but React's JSX typings don't model on <link>.
  const linkProps = {
    rel: "preload",
    as: "image",
    href: src,
    fetchpriority: "high",
    ...(imageSrcSet ? { imagesrcset: imageSrcSet, imagesizes: sizes, type } : {}),
  } as unknown as React.LinkHTMLAttributes<HTMLLinkElement>;

  return (
    <Helmet>
      <link {...linkProps} />
    </Helmet>
  );
};

export default HeroPreload;
